import React from 'react';
import {
  Show,
  SimpleShowLayout,
  TextField,
  DateField,
  NumberField,
  BooleanField,
  ReferenceField,
  TopToolbar,
  EditButton,
  ListButton,
} from 'react-admin';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ResourceBreadcrumbs from '../../components/ResourceBreadcrumbs';

const fileTypeIcons: Record<string, React.ReactElement> = {
  receipt: <ReceiptIcon />,
  photo: <ImageIcon />,
  report: <AssignmentIcon />,
  invoice: <DescriptionIcon />,
  default: <AttachFileIcon />,
};

const fileTypes: Record<string, string> = {
  receipt: 'Receipt',
  photo: 'Photo',
  report: 'Service Report',
  invoice: 'Invoice',
  document: 'Document',
  other: 'Other',
};

const MaintenanceShowActions = () => (
  <TopToolbar>
    <ListButton />
    <EditButton />
  </TopToolbar>
);

const MaintenanceFilesField = ({ record }: { record?: any }) => {
  const files = record?.files || [];
  
  const handleDownload = (file: any) => {
    if (file.file?.url) {
      window.open(file.file.url, '_blank');
    }
  };
  
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown size';
    const mb = bytes / (1024 * 1024);
    return mb < 1 ? `${(bytes / 1024).toFixed(1)} KB` : `${mb.toFixed(1)} MB`;
  };
  
  if (files.length === 0) {
    return (
      <Alert severity="info" variant="outlined">
        No files attached to this maintenance record.
      </Alert>
    );
  }
  
  return (
    <List>
      {files.map((file: any, index: number) => (
        <ListItem
          key={file.id || index}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            mb: 1,
            '&:last-child': { mb: 0 },
          }}
        >
          <ListItemIcon>
            {fileTypeIcons[file.fileType || 'default']}
          </ListItemIcon>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1">
                  {file.description || file.file?.name || 'Unnamed file'}
                </Typography>
                <Chip
                  label={fileTypes[file.fileType] || 'File'}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
            }
            secondary={
              <Typography variant="body2" color="text.secondary">
                {file.file?.name} • {formatFileSize(file.file?.size)}
              </Typography>
            }
          />
          <ListItemSecondaryAction>
            <IconButton
              edge="end"
              aria-label="download"
              onClick={() => handleDownload(file)}
              disabled={!file.file?.url}
            >
              <DownloadIcon />
            </IconButton>
          </ListItemSecondaryAction>
        </ListItem>
      ))}
    </List>
  );
};

export const EquipmentMaintenanceShow = () => (
  <Show actions={<MaintenanceShowActions />}>
    <ResourceBreadcrumbs />
    <SimpleShowLayout>
      <Box sx={{ width: '100%', maxWidth: 1000 }}>
        {/* Equipment & Service Information */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Service Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Equipment</Typography>
              <ReferenceField source="equipmentId" reference="equipment" link="show">
                <TextField source="name" />
              </ReferenceField>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Service Date</Typography>
              <DateField source="serviceDate" />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Service Type</Typography>
              <TextField source="serviceType" />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Scheduled Service</Typography>
              <BooleanField source="scheduledService" />
            </Grid>
          </Grid>
        </Paper>

        {/* Service Provider */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Service Provider
          </Typography>
          <ReferenceField source="serviceProviderId" reference="service-providers" link="show">
            <TextField source="name" />
          </ReferenceField>
        </Paper>

        {/* Mileage & Hours */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Mileage & Hours at Service
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Service Mileage</Typography>
              <NumberField source="serviceMileage" />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Engine Hours</Typography>
              <NumberField source="serviceEngineHours" />
            </Grid>
          </Grid>
        </Paper>

        {/* Service Details */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Service Details
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Service Description</Typography>
              <TextField source="description" />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Additional Notes</Typography>
              <TextField source="notes" />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Labor Hours</Typography>
              <NumberField source="laborHours" />
            </Grid>
          </Grid>
        </Paper>

        {/* Cost Information */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Cost Breakdown
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" color="text.secondary">Parts Cost</Typography>
              <NumberField source="partsCost" options={{ style: 'currency', currency: 'USD' }} />
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" color="text.secondary">Labor Cost</Typography>
              <NumberField source="laborCost" options={{ style: 'currency', currency: 'USD' }} />
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" color="text.secondary">Other Costs</Typography>
              <NumberField source="otherCost" options={{ style: 'currency', currency: 'USD' }} />
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" color="text.secondary">Total Cost</Typography>
              <NumberField source="totalCost" options={{ style: 'currency', currency: 'USD' }} />
            </Grid>
          </Grid>
        </Paper>

        {/* Invoice & Warranty */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Invoice & Warranty
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Invoice Number</Typography>
              <TextField source="invoiceNumber" />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Under Warranty</Typography>
              <BooleanField source="underWarranty" />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Warranty Claim Number</Typography>
              <TextField source="warrantyClaimNumber" />
            </Grid>
          </Grid>
        </Paper>

        {/* File Attachments */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 2 }}>
            File Attachments
          </Typography>
          <MaintenanceFilesField />
        </Paper>

        {/* Next Service */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Next Service Due
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Next Service Due Date</Typography>
              <DateField source="nextServiceDue" />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Next Service Mileage</Typography>
              <NumberField source="nextServiceMileage" />
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </SimpleShowLayout>
  </Show>
);